import { gql, useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import DefaultErrorPage from "next/error";
import Head from "next/head";
import React from "react";
import { ProductIdTypeEnum, ProductTypesEnum } from "__generated__/globalTypes";
import { BackButton } from "components/common/Button";
import Container from "components/common/Container";
import CreditHoursBadge from "components/common/CreditHoursBadge/CreditHoursBadge";
import Date from "components/common/Date";
import ErrorPage from "components/common/ErrorPage";
import Heading from "components/common/Heading";
import Link from "components/common/Link";
import { StripeCheckoutStatus } from "components/common/PurchaseModal/StripeCheckoutStatus";
import {
  CERTIFICATE_PROGRAMS_SLUG,
  COURSE_CATALOG_URL,
  COURSE_URL,
  MY_COURSES_URL,
} from "constants/urls";
import { mapNodes } from "helpers/mapNodes";
import { useUserContext } from "hooks";
import { usePageMeta } from "hooks/usePageMeta";
import { useRouterQuery } from "hooks/useRouterQuery";
import CollapsibleHtmlContent from "../common/CollapsibleHtmlContent";
import HtmlContent from "../common/HtmlContent";
import CourseTitle from "./CourseTitle";
import ProductSidebar from "./ProductSidebar";
import {
  ProductBundleViewQueryProduct,
  ProductBundleViewQueryQuery as ProductBundleViewQuery,
  ProductBundleViewQueryVariables,
} from "./__generated__/ProductBundle";
import styles from "./Course.module.css";

export type { ProductBundleViewQuery, ProductBundleViewQueryVariables };

// TODO: Separate out the user query and cache the view query
export const PRODUCT_BUNDLE_VIEW_QUERY = gql`
  query ProductBundleViewQuery($id: ID!, $idType: ProductIdTypeEnum!) {
    product(id: $id, idType: $idType) {
      id
      sku
      databaseId
      name
      description
      priceUSD
      couponForUser {
        code
        discountedPriceUSD
      }
      userDiscountsFromSubscription {
        id
        displayName
        percent
        fixedPrice
        subscriptionId
        productFeatureId
        usageLimit
        usageCount
        ceLimit
        ceCount
      }
      hasPurchased
      canPurchaseWithCredits
      creditCost
      type
      seo {
        title
        metaDesc
        metaKeywords
        canonical
      }
      seoCollapsibleContent {
        html
      }
      productCategories {
        edges {
          node {
            id
            slug
            parent {
              node {
                id
                slug
              }
            }
          }
        }
      }
      bundleFields {
        notes
      }
      availability {
        isPurchasable
      }
      courses {
        id
        title
        slug
        ceUnits
        priceUSD
        courseProgress
        isStarted
        startedOn
        completedOn
      }
      courseGroups {
        id
        title
        date
        hasCertificate
        isEnrolled
        isExpired
        courseGroupFields {
          programCode
        }
        subscriptionFeatureOptIns {
          membersExclusive
        }
      }
    }
  }
`;

export type ProductBundleParams = {
  slug: string;
};

interface ArticleProps {
  productBundle: ProductBundleViewQueryProduct;
}

const Article = ({ productBundle }: ArticleProps) => {
  const productCategorySlugs = mapNodes(productBundle?.productCategories, node =>
    node.parent ? node.parent.node?.slug : node.slug
  ).filter(notEmpty);

  const productCategorySlug = productCategorySlugs.length
    ? productCategorySlugs[0]
    : CERTIFICATE_PROGRAMS_SLUG;

  const courseGroup = productBundle?.courseGroups[0];
  const productName = courseGroup?.title || productBundle?.name;
  const hasCertificate = courseGroup?.hasCertificate;

  return (
    <article className={styles.courseArticleContainer}>
      <div className={styles.courseArticleTextContainer}>
        <BackButton backToHref={makeUrl(COURSE_CATALOG_URL, { qualifier: productCategorySlug })}>
          {hasCertificate ? "Back to Certificate Programs" : "Back to Packages"}
        </BackButton>
        <CourseTitle>{productName}</CourseTitle>
        <HtmlContent>{productBundle.description || ""}</HtmlContent>
        <Heading tag="h3">
          {hasCertificate ? "Certificate Program" : "Package"} is comprised of the following
          courses:
        </Heading>
        <div>
          {productBundle.courses.map(course => {
            return (
              <div key={course.id} className={styles.course}>
                <Link
                  as={makeUrl(COURSE_URL, { slug: course.slug as string, page: "" })}
                  href="/courses/[slug]"
                  className={styles.courseTitleLink}
                >
                  {course.title}
                </Link>
                <div className={styles.creditHoursBadge}>
                  <CreditHoursBadge size="sm" ceUnits={course.ceUnits} />
                </div>
              </div>
            );
          })}
        </div>
        {productBundle.seoCollapsibleContent?.html && (
          <div className={styles.seoContentWrapper}>
            <CollapsibleHtmlContent html={productBundle.seoCollapsibleContent.html} />
          </div>
        )}
        {productBundle.bundleFields?.notes && (
          <HtmlContent>{productBundle.bundleFields?.notes}</HtmlContent>
        )}
        <div className={styles.courseMetaData}>
          {courseGroup?.courseGroupFields?.programCode && (
            <div title="Program Code" className={styles.courseProgramCode}>
              {courseGroup.courseGroupFields.programCode}
            </div>
          )}
          {courseGroup?.date && (
            <div title="Published Date">
              <Date dateString={courseGroup.date || ""} />
            </div>
          )}
        </div>
      </div>
      <div className={styles.courseSidebarContainer}>
        <ProductSidebar
          product={productBundle}
          isMembersExclusive={courseGroup?.subscriptionFeatureOptIns?.membersExclusive || false}
        />
      </div>
    </article>
  );
};

const ProductBundle = () => {
  const {
    routerQuery: { slug },
  } = useRouterQuery<ProductBundleParams>();
  const { currentUser } = useUserContext();

  const { data, loading } = useEnhancedQuery<
    ProductBundleViewQuery,
    ProductBundleViewQueryVariables
  >(PRODUCT_BUNDLE_VIEW_QUERY, {
    variables: {
      id: slug,
      idType: ProductIdTypeEnum.SLUG,
    },
  });

  const productBundle = data?.product || null;

  usePageMeta(productBundle?.name, productBundle?.seo);

  if (!loading && productBundle?.type !== ProductTypesEnum.BUNDLE) {
    // Not viewing a product bundle
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <DefaultErrorPage statusCode={404} />
      </>
    );
  }

  const courseGroup = productBundle?.courseGroups?.[0];

  if (courseGroup?.isExpired && (!currentUser || !courseGroup.isEnrolled)) {
    return (
      <ErrorPage
        errorCode="403"
        message="This package has expired"
        description="If you are enrolled in this package, please login to access it."
      />
    );
  }

  return (
    <Container>
      <StripeCheckoutStatus onSuccessRedirectUrl={MY_COURSES_URL} loading={loading}>
        {productBundle && <Article productBundle={productBundle} />}
      </StripeCheckoutStatus>
    </Container>
  );
};

export default ProductBundle;
